import {FC, useEffect, useRef, useState} from "react";
import {App, Button, Modal, Space, Typography} from "antd";
import {QuestionCircleOutlined, TrophyOutlined} from "@ant-design/icons";
import {useNavigate} from "react-router-dom";
import AxiosServices from "../Services/axiosServices";
import {useAuth} from "../config/AuthContext";
import {useGlobal} from "../config/GlobalContext";

const {Text, Paragraph} = Typography;

// Pops a modal once per (session, user, competition) when the user
// logs in and has at least one unanswered pre-competition bonus bet.
// Mounted globally in SBLayout alongside the chat notifier so it
// triggers on every fresh page load while the conditions are met.
const CompetitionBetsReminder: FC = () => {
    const {isLoggedIn, authUser} = useAuth();
    const {currentCmpEdt} = useGlobal();
    const navigate = useNavigate();
    const {message} = App.useApp();
    const [missingCount, setMissingCount] = useState<number | null>(null);
    const [open, setOpen] = useState(false);

    // Track the previous isLoggedIn so we can clear the "already shown"
    // sessionStorage flag on a login transition. Without this, a
    // disconnect/reconnect cycle within the same tab kept the flag set
    // and the reminder never reappeared.
    const wasLoggedInRef = useRef<boolean>(isLoggedIn);
    useEffect(() => {
        const justLoggedIn = !wasLoggedInRef.current && isLoggedIn;
        wasLoggedInRef.current = isLoggedIn;
        if (justLoggedIn) {
            const prefix = 'cmpedt-bets-reminder:';
            for (let i = sessionStorage.length - 1; i >= 0; i--) {
                const k = sessionStorage.key(i);
                if (k && k.startsWith(prefix)) sessionStorage.removeItem(k);
            }
        }
    }, [isLoggedIn]);

    useEffect(() => {
        if (!isLoggedIn || !authUser?.idt || !currentCmpEdt) {
            setOpen(false);
            return;
        }
        const flagKey = `cmpedt-bets-reminder:${authUser.idt}:${currentCmpEdt}`;
        if (sessionStorage.getItem(flagKey) === 'shown') return;

        let cancelled = false;
        AxiosServices.listCmpEdtBets(currentCmpEdt)
            .then((rows) => {
                if (cancelled) return;
                const missing = (rows ?? []).filter((r) => !r.userAnswer).length;
                if (missing > 0) {
                    setMissingCount(missing);
                    setOpen(true);
                    sessionStorage.setItem(flagKey, 'shown');
                }
            })
            .catch((err) => {
                // Quiet — the reminder is a nudge, not a critical flow.
                console.warn('CompetitionBetsReminder: listCmpEdtBets failed', err);
            });
        return () => { cancelled = true; };
    }, [isLoggedIn, authUser?.idt, currentCmpEdt]);

    if (!open || missingCount == null) return null;

    return (
        <Modal
            open
            title={
                <Space>
                    <TrophyOutlined/>
                    <span>Paris de compétition incomplets</span>
                </Space>
            }
            footer={null}
            closable
            onCancel={() => setOpen(false)}
            maskClosable
        >
            <Paragraph>
                <Text strong>{missingCount}</Text> pari{missingCount > 1 ? 's' : ''} de compétition
                {missingCount > 1 ? ' restent' : ' reste'} à saisir.
            </Paragraph>
            <Paragraph type="secondary" style={{fontSize: 13}}>
                <QuestionCircleOutlined style={{marginRight: 6}}/>
                Rends-toi dans <Text strong>Compétition → Paris avant la compétition</Text> pour répondre aux questions
                (vainqueur, finaliste, meilleur buteur, etc.).
            </Paragraph>
            <Space style={{marginTop: 12, width: '100%', justifyContent: 'flex-end'}}>
                <Button onClick={() => setOpen(false)}>Plus tard</Button>
                <Button
                    type="primary"
                    onClick={() => {
                        setOpen(false);
                        navigate('/competition/bets');
                        message.info('Saisis tes paris ici.');
                    }}
                >
                    Saisir maintenant
                </Button>
            </Space>
        </Modal>
    );
};

export default CompetitionBetsReminder;
